import { UseMutationResult } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import React from 'react';
import type { MouseEventHandler, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

import { HorizontalLayout } from '../Layout';
import { Spinner } from '../Spinner';
import { Text } from '../Text';

export type ButtonSize = keyof typeof sizes;

export type ButtonProps<TData = unknown> = (
    | {
          buttonType?: ThemeButtonOptions;
          size?: ButtonSize;
          waiting?: boolean;
          onClick?: never;
          mutation?: UseMutationResult<any, any, TData, any>;
          mutationVar?: TData;
          icon?: JSX.Element;
      }
    | {
          buttonType?: ThemeButtonOptions;
          size?: ButtonSize;
          waiting?: boolean;
          onClick?: MouseEventHandler<HTMLButtonElement>;
          mutation?: never;
          mutationVar?: never;
          icon?: JSX.Element;
      }
) &
    React.ButtonHTMLAttributes<HTMLButtonElement>;

/** @deprecated */
export const Button = <TData,>({
    children,
    disabled: _disabled,
    waiting: _waiting,
    mutation,
    mutationVar,
    onClick,
    type = 'button',
    size,
    icon,
    buttonType,
    ...props
}: PropsWithChildren<ButtonProps<TData>>) => {
    const waiting = _waiting || (mutation?.isPending && isEqual(mutation.variables, mutationVar)) || false;
    const disabled = _disabled || waiting;

    return (
        <StyledButton
            type={type}
            disabled={disabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                onClick ? onClick(e) : mutation && mutationVar && mutation.mutate(mutationVar)
            }
            $size={size}
            $buttonType={buttonType}
            {...props}
        >
            <ButtonContent waiting={waiting} icon={icon} size={size}>
                {children}
            </ButtonContent>
        </StyledButton>
    );
};

const ButtonContent = ({
    waiting,
    icon,
    children,
    size,
}: {
    waiting: boolean;
    icon?: JSX.Element;
    children?: string | React.ReactNode;
    size?: ButtonSize;
}) => {
    if (waiting) {
        return <Spinner size="medium" />;
    }

    if (icon) {
        return (
            <HorizontalLayout $gap={8} $rowCenter>
                {icon}
                {children && <Text largeBody={size === 'large'}>{children}</Text>}
            </HorizontalLayout>
        );
    }

    if (typeof children === 'string') {
        return <Text largeBody={size === 'large'}>{children}</Text>;
    }

    return children;
};

const StyledButton = styled.button<{ $buttonType?: ThemeButtonOptions; $size?: ButtonSize }>`
    display: flex;
    justify-content: center;
    align-items: center;
    user-select: none;
    cursor: pointer;

    font-size: 1rem;
    font-weight: 400;

    border-style: solid;
    border-width: 1px;
    border-radius: 32px;

    ${({ theme, $buttonType = 'primary' }) => $buttonType && buttonStyles(theme.buttons[$buttonType])}
    ${({ $size = 'medium' }) => sizes[$size]}

    &:disabled {
        pointer-events: none;

        background: ${({ theme }) => theme.colors.cocoa200};
        color: ${({ theme }) => theme.colors.cocoa800};
        border-color: ${({ theme }) => theme.colors.cocoa200};
        opacity: 0.4;

        &:hover {
            background: ${({ theme }) => theme.colors.cocoa200};
        }
    }
`;

const sizes = {
    micro: css`
        border-radius: 3px;
    `,
    icon: css`
        padding: 4px;
        border-radius: 3px;
    `,
    small: css`
        padding: 4px 16px;
    `,
    medium: css`
        padding: 8px 24px;
        min-width: 120px;
    `,
    large: css`
        padding: 12px 48px;
        width: 100%;
    `,
};

const buttonStyles = ({ background, text, border, hoverBackground, hoverBorder }: ButtonTheme) => {
    return css`
        background-color: ${background};
        color: ${text};
        border-color: ${border};

        &:hover {
            background-color: ${hoverBackground};
            border-color: ${hoverBorder};
        }
    `;
};
